import { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StayCloseSection() {
  const [form, setForm] = useState({ name: '', email: '', mobile: '', city: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) e.target.querySelectorAll('.reveal-el').forEach(c => c.classList.add('visible')); }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email required.'); return; }
    setLoading(true);
    try { await axios.post(`${API}/subscribe`, form); setDone(true); } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <section data-testid="stay-close-section" ref={ref} className="py-24 md:py-40 px-5 md:px-10 lg:px-20 bg-stayclose">
      <div className="max-w-2xl mx-auto text-center">
        <p className="reveal-el font-['Cormorant_Garamond'] text-xs tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--gold)' }}>Stay Close</p>
        <h2 className="reveal-el font-['Playfair_Display'] italic text-3xl md:text-4xl font-normal mb-4" style={{ color: 'var(--warm-cream)' }}>
          Stay close<span style={{ color: 'var(--gold)', opacity: 0.4 }}>.</span>
        </h2>
        <p className="reveal-el font-['Inter'] text-sm mb-12" style={{ color: 'var(--ash)' }}>Rare updates. New work. Nothing more.</p>

        {done ? (
          <div className="reveal-el"><p className="font-['Playfair_Display'] italic text-2xl" style={{ color: 'var(--gold)' }}>You're inside.</p></div>
        ) : (
          <form onSubmit={handleSubmit} className="reveal-el max-w-sm mx-auto space-y-6">
            <input data-testid="sc-name" type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] focus:ring-0 focus:outline-none" style={{ borderColor: 'rgba(201,168,76,0.12)', color: 'var(--warm-cream)' }} />
            <input data-testid="sc-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] focus:ring-0 focus:outline-none" style={{ borderColor: 'rgba(201,168,76,0.12)', color: 'var(--warm-cream)' }} />
            {/* Phone — optional, WhatsApp */}
            <div className="relative">
              <input data-testid="sc-mobile" type="tel" value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} placeholder="Phone (optional)" className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] focus:ring-0 focus:outline-none" style={{ borderColor: 'rgba(201,168,76,0.08)', color: 'var(--warm-cream)' }} />
              <p className="flex items-center gap-1.5 mt-1.5 text-[10px]" style={{ color: 'var(--ash)', opacity: 0.5 }}>
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" style={{ color: '#25D366' }}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.387 0-4.592-.836-6.313-2.228l-.44-.363-3.095 1.038 1.038-3.095-.363-.44A9.965 9.965 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
                For WhatsApp updates (optional)
              </p>
            </div>
            <button data-testid="sc-submit" type="submit" disabled={loading} className="font-['Inter'] text-xs tracking-[0.15em] uppercase px-8 py-3 rounded-full transition-all disabled:opacity-50" style={{ background: 'var(--gold)', color: 'var(--deep-black)' }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Enter'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
